// src/components/home/FeatureShowcase.jsx
// The "Everything a manuscript needs, layered together" row on the Home page. Ported from
// the owner's design mock: each card shows a hover popover with a miniature mockup of that
// part of the real app, and clicking the card opens the same mockup larger in a modal.
// These are illustrative mockups (built from sx, not screenshots) — swap in real screen
// captures in DEMOS/POPOVER content whenever they exist.
import { useRef, useState } from 'react';
import { Box, Typography, Dialog, IconButton, Popper, Fade, useMediaQuery } from '@mui/material';
import { keyframes, useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import {
  MenuBook,
  Forum,
  CallSplit,
  Palette,
  PlayArrow,
  Close,
  Image as ImageIcon,
  FiberManualRecord,
} from '@mui/icons-material';

// Four distinct purple shades for the cards, each at the same 0.88 opacity as the other
// dark cards on the page.
const PURPLE_SHADES = [
  'rgba(94,84,196,0.88)',  // lighter
  'rgba(65,59,141,0.88)',  // primary.main
  'rgba(46,42,102,0.88)',  // darker
  'rgba(30,27,66,0.88)',   // darkest
];

const CARD_BORDER = '0.5px solid rgba(255,255,255,0.14)';

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

// The 10px rise the popover used to get from its transform transition.
const popIn = keyframes`
  from { transform: translateY(-10px); }
  to   { transform: translateY(0); }
`;

/**
 * Colours that exist only inside the mini mockups.
 *
 * These depict OBJECTS, not UI: a cork pinboard and its thread, a cloth book
 * cover and its spine, the paper a studio shot sits on, the lilac/cream/rose of
 * three index cards. They are illustration content in the same way a photograph
 * would be, they appear nowhere else in the product, and promoting them to
 * src/theme.js would grow the shared palette with values no other screen can
 * ever reuse — the opposite of what that file is for.
 *
 * Anything in these mockups that depicts the REAL app — body ink, muted text,
 * hairlines, the gold highlight ink, the comment tile — uses the token instead,
 * because those are the product's own colours being drawn at small scale.
 *
 * The repeated allow markers are not redundant: check-design-tokens.mjs matches
 * the marker on a line or the single line above it, so one comment cannot cover
 * a whole block.
 */
const MOCKUP = {
  // design-tokens-allow — fake window chrome around each mockup
  chromeBar: '#EDE7F5', chromeDot: '#C9BEDD',
  // design-tokens-allow — editor sidebar ground and the prose it frames
  sidebar: '#2C1650', prose: '#3A3937', textLine: '#EDE9F2', highlight: '#F3E9CE',
  // design-tokens-allow — the commenter in the dialogue mockup
  avatar: '#6B4FA0', timestamp: '#9A9591', commentBody: '#4B4A47',
  // design-tokens-allow — three index cards pinned to the board
  pinLilac: '#E9E1F5', pinLilacInk: '#3C1F63', pinCream: '#FBF3DD',
  // design-tokens-allow — and the third card, plus the board and thread themselves
  pinRose: '#F3E0E4', pinRoseInk: '#6B2438', cork: '#C08A56', thread: '#B23A28',
  // design-tokens-allow — the physical book in the studio mockup
  studioGround: '#EFE9DC', cloth: '#5A7D5E', clothSpine: '#3F5940',
};

// Shared "screen" chrome (the three-dot titlebar) wrapping every mini mockup below.
const VizFrame = ({ width = 260, children }) => (
  <Box sx={{ width, borderRadius: 1.25, overflow: 'hidden', border: `0.5px solid ${tokens.color.line}`, boxShadow: '0 6px 16px rgba(26,26,26,0.08)', bgcolor: '#FFFFFF' }}>
    <Box sx={{ height: 16, bgcolor: MOCKUP.chromeBar, display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: MOCKUP.chromeDot }} />
      ))}
    </Box>
    {children}
  </Box>
);

const OUTLINE_ITEMS = [
  { num: '1.', text: 'New Chapter', active: true },
  { num: '2.', text: 'Actor 1 in Shop' },
  { num: '3.', text: 'Actor 2 in…' },
  { num: '4.', text: 'New Chapter II' },
];

const CompositionViz = () => (
  <VizFrame>
    <Box sx={{ display: 'flex', height: 122 }}>
      <Box sx={{ width: '36%', bgcolor: MOCKUP.sidebar, p: '9px 7px' }}>
        <Typography sx={{ fontSize: 5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.32)', mb: 0.75 }}>
          Outline
        </Typography>
        {OUTLINE_ITEMS.map((item) => (
          <Box
            key={item.num}
            sx={{
              display: 'flex', gap: 0.5, alignItems: 'center', px: 0.5, py: '3px', borderRadius: '3px', mb: '2px',
              bgcolor: item.active ? 'rgba(184,149,46,0.28)' : 'transparent',
            }}
          >
            <Typography sx={{ fontSize: 6, color: item.active ? MOCKUP.highlight : 'rgba(255,255,255,0.35)', fontWeight: item.active ? 700 : 400, flexShrink: 0 }}>
              {item.num}
            </Typography>
            <Typography sx={{ fontSize: 6.5, color: item.active ? MOCKUP.highlight : 'rgba(255,255,255,0.55)', fontWeight: item.active ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1, bgcolor: '#FFFFFF', p: '10px 12px', overflow: 'hidden' }}>
        <Typography sx={{ fontFamily: 'Cambria, Georgia, serif', fontSize: 10, fontWeight: 700, color: tokens.color.ink, mb: '2px' }}>
          New Chapter
        </Typography>
        <Typography sx={{ fontFamily: 'Cambria, Georgia, serif', fontSize: 7.5, fontStyle: 'italic', color: tokens.color.muted, mb: 1 }}>
          Actor 1 in shop
        </Typography>
        <Box sx={{ height: 3.5, borderRadius: '2px', bgcolor: MOCKUP.textLine, width: '92%', mb: '5px' }} />
        <Box sx={{ height: 3.5, borderRadius: '2px', bgcolor: MOCKUP.textLine, width: '60%', mb: '5px' }} />
        <Box sx={{ height: 3.5, borderRadius: '2px', bgcolor: MOCKUP.highlight, width: '78%' }} />
      </Box>
    </Box>
  </VizFrame>
);

const DialogueViz = () => (
  <VizFrame>
    <Box sx={{ p: '12px', height: 122, bgcolor: '#FFFFFF' }}>
      <Typography sx={{ fontFamily: 'Cambria, Georgia, serif', fontSize: 9, lineHeight: 1.7, color: MOCKUP.prose, mb: 1 }}>
        She stood at the edge of the{' '}
        <Box component="span" sx={{ bgcolor: MOCKUP.highlight, color: tokens.color.goldInk, px: '2px', borderRadius: '2px' }}>
          quiet harbor
        </Box>
        , watching the tide climb the wall.
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.875, alignItems: 'flex-start', bgcolor: tokens.color.purpleTint, borderRadius: 1.125, p: '7px 8px', mb: 1 }}>
        <Box sx={{ width: 17, height: 17, borderRadius: '50%', bgcolor: MOCKUP.avatar, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 6.5, color: '#FFFFFF', fontWeight: 700, flexShrink: 0 }}>
          JA
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
            <Typography sx={{ fontSize: 7.5, fontWeight: 700, color: tokens.color.ink }}>J. Alvarez</Typography>
            <Typography sx={{ fontSize: 6.5, color: MOCKUP.timestamp }}>2m ago</Typography>
          </Box>
          <Typography sx={{ fontSize: 8, color: MOCKUP.commentBody, lineHeight: 1.5 }}>
            Tighter image here, maybe &ldquo;still harbor&rdquo;?
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.625 }}>
        <Box sx={{ fontSize: 7, fontWeight: 700, px: '9px', py: '3.5px', borderRadius: '5px', bgcolor: 'primary.main', color: '#FFFFFF' }}>
          Accept
        </Box>
        <Box sx={{ fontSize: 7, fontWeight: 700, px: '9px', py: '3.5px', borderRadius: '5px', bgcolor: '#FFFFFF', color: tokens.color.muted, border: `0.5px solid ${tokens.color.line}` }}>
          Reject
        </Box>
      </Box>
    </Box>
  </VizFrame>
);

const BRANCH_CARDS = [
  { label: 'Maya', left: 12, top: 38, bg: MOCKUP.pinLilac, color: MOCKUP.pinLilacInk },
  { label: 'Ch. 2', left: 82, top: 14, bg: MOCKUP.pinCream, color: tokens.color.goldInk },
  { label: 'The Sign', left: 96, top: 74, bg: MOCKUP.pinRose, color: MOCKUP.pinRoseInk },
];

const BranchViz = () => (
  <VizFrame>
    <Box
      sx={{
        height: 122,
        position: 'relative',
        backgroundColor: MOCKUP.cork,
        backgroundImage:
          'radial-gradient(circle at 18% 24%, rgba(0,0,0,0.09) 0 1.4px, transparent 1.4px), radial-gradient(circle at 62% 66%, rgba(0,0,0,0.08) 0 1.4px, transparent 1.4px), radial-gradient(circle at 82% 18%, rgba(0,0,0,0.07) 0 1.4px, transparent 1.4px)',
        backgroundSize: '15px 15px, 19px 19px, 17px 17px',
      }}
    >
      <Box sx={{ position: 'absolute', width: 52, height: '1.4px', bgcolor: MOCKUP.thread, left: 66, top: 52, transform: 'rotate(-14deg)', transformOrigin: 'left center', opacity: 0.85 }} />
      <Box sx={{ position: 'absolute', width: 60, height: '1.4px', bgcolor: MOCKUP.thread, left: 66, top: 52, transform: 'rotate(22deg)', transformOrigin: 'left center', opacity: 0.85 }} />
      {BRANCH_CARDS.map((card) => (
        <Box
          key={card.label}
          sx={{
            position: 'absolute', left: card.left, top: card.top, width: 50, px: '7px', py: '6px',
            borderRadius: '2px', fontSize: 6.5, fontWeight: 700, lineHeight: 1.3,
            boxShadow: '0 4px 8px rgba(26,16,8,0.35)', bgcolor: card.bg, color: card.color,
          }}
        >
          {card.label}
        </Box>
      ))}
    </Box>
  </VizFrame>
);

const StudioViz = () => (
  <VizFrame>
    <Box sx={{ height: 122, bgcolor: MOCKUP.studioGround, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ width: 58, height: 96, bgcolor: MOCKUP.cloth, borderRadius: '3px 0 0 3px', p: '8px 7px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ height: '2.2px', borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.42)', width: '80%' }} />
        <Box sx={{ height: '2.2px', borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.42)', width: '65%' }} />
        <Box sx={{ height: '2.2px', borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.42)', width: '72%' }} />
        <Box sx={{ mt: 'auto', height: 11, background: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 2.4px)' }} />
      </Box>
      <Box sx={{ width: 7, height: 96, bgcolor: MOCKUP.clothSpine }} />
      <Box sx={{ width: 58, height: 96, bgcolor: MOCKUP.cloth, borderRadius: '0 3px 3px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.625, p: '6px' }}>
        <Box sx={{ width: '72%', height: 4, bgcolor: MOCKUP.highlight, borderRadius: '1px' }} />
        <Box sx={{ width: '44%', height: '2.2px', bgcolor: 'rgba(255,255,255,0.65)', borderRadius: '1px' }} />
        <Box sx={{ width: '58%', aspectRatio: '1', bgcolor: 'rgba(255,255,255,0.16)', border: '1px dashed rgba(255,255,255,0.45)', borderRadius: '2px', mt: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ImageIcon sx={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }} />
        </Box>
      </Box>
    </Box>
  </VizFrame>
);

const FEATURES = [
  {
    id: 'compose',
    label: 'Composition engine',
    Icon: MenuBook,
    iconColor: tokens.color.goldLight,
    cardBg: PURPLE_SHADES[0],
    popIconBg: 'rgba(255,255,255,0.08)',
    popIconColor: tokens.color.goldLight,
    hintColor: tokens.color.goldLight,
    desc: 'Where the manuscript lives. A document outline on the left, your page on the right, exactly like the editor itself.',
    modalSub: 'Distraction-free drafting with your outline always in view',
    modalDesc: 'Every chapter lives in the outline on the left. Select one and the page updates instantly on the right, exactly as it does inside the real editor.',
    Viz: CompositionViz,
  },
  {
    id: 'dialogue',
    label: 'Dialogue interface',
    Icon: Forum,
    iconColor: 'secondary.main',
    cardBg: PURPLE_SHADES[1],
    popIconBg: 'rgba(255,255,255,0.08)',
    popIconColor: 'secondary.main',
    hintColor: 'secondary.main',
    desc: 'Where reviewers respond. Suggested edits highlight inline, with a threaded comment and one-click accept beside your text.',
    modalSub: 'Threaded review, without leaving the page',
    modalDesc: 'Reviewers highlight a phrase, leave a note, and you accept or reject it inline. Nothing routes through email or a separate app.',
    Viz: DialogueViz,
  },
  {
    id: 'branch',
    label: 'Idea branching',
    Icon: CallSplit,
    iconColor: 'secondary.main',
    cardBg: PURPLE_SHADES[2],
    popIconBg: 'rgba(255,255,255,0.08)',
    popIconColor: 'secondary.main',
    hintColor: 'secondary.main',
    desc: 'Where ideas fork. Pin characters, scenes, and artifacts to a corkboard, connect them with a string, and branch an alternate version any time.',
    modalSub: 'The corkboard behind every manuscript',
    modalDesc: 'Pin characters, scenes, and artifacts to a corkboard, connect them with a string, and branch an alternate version whenever you want to try something new.',
    Viz: BranchViz,
  },
  {
    id: 'studio',
    label: 'Production studio',
    Icon: Palette,
    iconColor: 'secondary.main',
    cardBg: PURPLE_SHADES[3],
    popIconBg: 'rgba(255,255,255,0.08)',
    popIconColor: 'secondary.main',
    hintColor: 'secondary.main',
    desc: 'Where the book takes shape. Design a wraparound cover, front to spine to back, and export print-ready files without leaving the workspace.',
    modalSub: 'From manuscript to print-ready cover',
    modalDesc: 'Design the full wraparound, front to spine to back, try color and trim options live, and export a print-ready file when it is right.',
    Viz: StudioViz,
  },
];

const FeatureTile = ({ feature, onOpen }) => {
  const { Icon, label, iconColor, cardBg, popIconBg, popIconColor, hintColor, desc, Viz } = feature;
  const theme = useTheme();

  // WHY POPPER AND NOT AN ABSOLUTE DIV
  //
  // This popover started as a hand-positioned `position: absolute` box at
  // `top: 100%` with an 18px top margin. That cannot work, for three reasons that
  // all come from the same root cause: an absolutely positioned element has no way
  // to know whether there is room for it on screen.
  //
  //   1. It always opened downward, so on a 900px-tall laptop it ran ~220px past
  //      the bottom of the window and its content was unreachable — scrolling to
  //      reach it moves the pointer off the card, which closes it.
  //   2. At 280px wide, centred on a ~201px card, the first and last cards pushed
  //      it past the viewport edge and gave the whole page a horizontal scrollbar
  //      between 900px and 1022px. It did this even with nobody hovering, because
  //      `visibility: hidden` still occupies layout.
  //   3. The 18px `mt` was margin, and margin is not a hover region. The pointer
  //      lost :hover 2px into the gap and the popover faded out before it could be
  //      reached, so nothing inside it was ever hoverable or clickable.
  //
  // Popper solves all three because it measures: `flip` turns the panel upward when
  // there is no room below, `preventOverflow` keeps it inside the viewport on the
  // cross axis, and portalling it to the body means it never contributes to page
  // layout or scroll width. The dead gap is closed by making the 18px a transparent
  // PADDING on the Popper itself rather than a margin outside it — padding is part
  // of the element, so the pointer stays within the popover while crossing it. The
  // padding follows the flip (top when the panel is below, bottom when above).
  //
  // Note this is the first direct Popper use in src/ — Tooltip and Menu build on it
  // internally, but nothing here anchors a custom panel yet. Follow this shape if
  // you need another one.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Open when the pointer is over either the card or the popover, so travelling
  // between them keeps it up. Replaces the old CSS-only
  // `& .feature-tile:hover .feature-popover` rule, which had no breakpoint gate:
  // on touch a tap latched :hover, and only `display: none` kept it off screen.
  // Gating on the breakpoint means it is never mounted on a phone at all.
  const anchorRef = useRef(null);
  const [overCard, setOverCard] = useState(false);
  const [overPopover, setOverPopover] = useState(false);
  const open = isDesktop && (overCard || overPopover);

  return (
    <Box
      ref={anchorRef}
      className="feature-tile"
      sx={{ position: 'relative', flex: { sm: '1 1 0' } }}
      onMouseEnter={() => setOverCard(true)}
      onMouseLeave={() => setOverCard(false)}
    >
      <Box
        onClick={() => onOpen(feature)}
        // The card gained a click action in this feature, so it needs the keyboard
        // equivalent a real button would have had. Enter and Space only, matching
        // native button behaviour.
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(feature);
          }
        }}
        sx={{
          bgcolor: cardBg,
          border: CARD_BORDER,
          color: '#FFFFFF',
          borderRadius: 3,
          p: 2.25,
          cursor: 'pointer',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          '&:hover': { transform: 'scale(1.05)', boxShadow: '0 12px 28px rgba(26,26,26,0.18)', zIndex: 10 },
        }}
      >
        <Icon sx={{ fontSize: 22, color: iconColor }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 1.25, color: '#FFFFFF' }}>{label}</Typography>
        <Typography sx={{ fontSize: 8, letterSpacing: '0.04em', textTransform: 'uppercase', mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.7, color: hintColor }}>
          <PlayArrow sx={{ fontSize: 10 }} /> Watch it work
        </Typography>
      </Box>

      {/* Hover popover — desktop only; touch users get the click-to-modal path below.
          Dark-mode chrome to match the rest of the page's cards. */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom"
        transition
        // Offset is zero because the 18px gap is the bridge padding below, not a
        // margin — see the note above. Keeping it here as margin would reopen the
        // dead-gap bug.
        modifiers={[
          { name: 'offset', options: { offset: [0, 0] } },
          { name: 'flip', options: { fallbackPlacements: ['top'], padding: 8 } },
          { name: 'preventOverflow', options: { altAxis: true, padding: 8 } },
        ]}
        sx={{
          // Above the page, below the Dialog (modal is 1300).
          zIndex: theme.zIndex.appBar,
          // The transparent bridge that keeps the pointer inside the popover while
          // it crosses the visual gap. Sits on whichever side faces the card.
          '&[data-popper-placement^="bottom"] .feature-popover-bridge': { pt: '18px' },
          '&[data-popper-placement^="top"] .feature-popover-bridge': { pb: '18px' },
        }}
        onMouseEnter={() => setOverPopover(true)}
        onMouseLeave={() => setOverPopover(false)}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={220}>
            <Box className="feature-popover-bridge">
              <Box
                className="feature-popover"
                sx={{
                  width: 280,
                  bgcolor: tokens.color.plum,
                  border: '0.5px solid rgba(255,255,255,0.14)',
                  borderRadius: 2,
                  boxShadow: '0 26px 54px rgba(0,0,0,0.4)',
                  // The 10px rise the absolute version animated in with. Fade owns the
                  // opacity; this keeps the movement.
                  animation: `${popIn} 0.22s ease`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '16px 18px 14px' }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: popIconBg }}>
                    <Icon sx={{ fontSize: 17, color: popIconColor }} />
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#FFFFFF' }}>{label}</Typography>
                </Box>
                <Box sx={{ px: '18px', pb: '18px' }}>
                  <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, mb: 1.75 }}>{desc}</Typography>
                  <Viz />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 10.5, fontWeight: 700, color: 'secondary.main', mt: 1.5 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PlayArrow sx={{ fontSize: 11, color: tokens.color.plum }} />
                    </Box>
                    Click card to watch
                  </Box>
                </Box>
              </Box>
            </Box>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default function FeatureShowcase() {
  const [openFeature, setOpenFeature] = useState(null);
  const theme = useTheme();
  // Below md there is no popover at all, so the hover half of this sentence
  // described something the reader could not do.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box sx={{ mt: { xs: 5, md: 8 }, mb: { xs: 6, md: 10 } }}>
      <Typography
        sx={{
          textAlign: 'center',
          fontFamily: 'Cambria, Georgia, serif',
          fontSize: { xs: 19, md: 22 },
          color: '#FFFFFF',
          mb: 1,
        }}
      >
        Everything a manuscript needs, layered together
      </Typography>
      <Typography sx={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.6)', mb: { xs: 4, md: 6 } }}>
        {isDesktop
          ? 'Hover a card for a closer look. Click it to watch how it works.'
          : 'Tap a card to watch how it works.'}
      </Typography>

      <Box
        sx={{
          display: { xs: 'grid', sm: 'flex' },
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        {FEATURES.map((feature) => (
          <FeatureTile key={feature.id} feature={feature} onOpen={setOpenFeature} />
        ))}
      </Box>

      <Dialog open={!!openFeature} onClose={() => setOpenFeature(null)} maxWidth="sm" fullWidth>
        {openFeature && (
          <Box sx={{ position: 'relative', bgcolor: '#FFFFFF' }}>
            <IconButton
              onClick={() => setOpenFeature(null)}
              aria-label="Close"
              sx={{ position: 'absolute', top: 16, right: 16, width: 30, height: 30, bgcolor: 'rgba(26,26,26,0.06)', zIndex: 5, '&:hover': { bgcolor: 'rgba(26,26,26,0.12)' } }}
            >
              <Close sx={{ fontSize: 16, color: tokens.color.muted }} />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '26px 28px 18px' }}>
              <Box sx={{ width: 42, height: 42, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: openFeature.popIconBg }}>
                <openFeature.Icon sx={{ fontSize: 20, color: openFeature.popIconColor }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'Cambria, Georgia, serif', fontSize: 19, fontWeight: 700, color: tokens.color.ink }}>
                  {openFeature.label}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: tokens.color.muted, mt: '2px' }}>{openFeature.modalSub}</Typography>
              </Box>
            </Box>

            <Box sx={{ mx: '28px', borderRadius: 1.5, overflow: 'hidden', position: 'relative', border: `0.5px solid ${tokens.color.line}` }}>
              <Box
                sx={{
                  position: 'absolute', top: 10, left: 10, bgcolor: 'rgba(26,26,26,0.72)', color: '#FFFFFF',
                  fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', px: 1.25, py: 0.625, borderRadius: 5,
                  display: 'flex', alignItems: 'center', gap: 0.625, zIndex: 5,
                }}
              >
                <FiberManualRecord sx={{ fontSize: 8, color: tokens.color.error, animation: `${blink} 1.4s step-end infinite` }} />
                LIVE PREVIEW
              </Box>
              <Box sx={{ width: '100%', height: 300, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: tokens.color.purpleTint }}>
                <Box sx={{ transform: 'scale(2.35)' }}>
                  <openFeature.Viz />
                </Box>
              </Box>
            </Box>

            <Typography sx={{ p: '20px 28px 28px', fontSize: 13, color: tokens.color.muted, lineHeight: 1.7 }}>
              {openFeature.modalDesc}
              <Box component="span" sx={{ display: 'block', fontSize: 10.5, color: tokens.color.purpleSoft, mt: 1.25, fontStyle: 'italic' }}>
                This is a live animated preview built from the real interface, not recorded footage. Swap in an actual screen capture any time.
              </Box>
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
